import React, { useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { companyInfoData, productsData } from "../services/apiService";
import { SEO } from "../components/SEO";
import {
  AnimatedInView,
  Section,
  GradientHero,
  GradientText,
  ProductCard,
  CTABand,
  staggerChild,
} from "../components/ui";
import {
  generateWebPageStructuredData,
  generateBreadcrumbStructuredData,
  generateProductListStructuredData,
} from "../utils/structuredData";
import { REACT_APP_SITE_URL, SITE_NAME } from "../config/seo";

const PAGE_DESCRIPTION =
  "Discover our range of high-quality ECR machines, POS systems, and billing solutions from top brands like Essae and WEP. Serving businesses across Delhi, India.";

const Products: React.FC = () => {
  const [category, setCategory] = useState<string>("All");
  // Static catalog data — synchronous for prerender/hydration parity
  const products = productsData;
  const companyInfo = companyInfoData;

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const filteredProducts =
    category === "All"
      ? products
      : products.filter((product) => product.category === category);

  return (
    <>
      <SEO
        title="Products"
        description={PAGE_DESCRIPTION}
        jsonLd={[
          generateWebPageStructuredData({
            title: `Products - ${SITE_NAME}`,
            description: PAGE_DESCRIPTION,
            url: `${REACT_APP_SITE_URL}/products`,
          }),
          generateBreadcrumbStructuredData([
            { name: "Home", item: `${REACT_APP_SITE_URL}/` },
            { name: "Products", item: `${REACT_APP_SITE_URL}/products` },
          ]),
          ...(products.length > 0
            ? [generateProductListStructuredData(products, `${REACT_APP_SITE_URL}/products`)]
            : []),
        ]}
      />

      <GradientHero
        compact
        eyebrow="Our Catalog"
        title={
          <>
            Products built for <GradientText>everyday business</GradientText>
          </>
        }
        subtitle={companyInfo?.productsTitle}
      />

      <Section py={{ xs: 4, md: 6 }}>
        {/* Category filter */}
        <Box
          sx={{
            mb: 5,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              onClick={() => setCategory(cat)}
              color={category === cat ? "primary" : "default"}
              variant={category === cat ? "filled" : "outlined"}
              sx={{
                px: 1.5,
                py: 2.2,
                fontSize: "0.9rem",
                "&:hover": { transform: "translateY(-1px)", boxShadow: 1 },
                transition: "all 0.2s",
              }}
            />
          ))}
        </Box>

        <AnimatedInView variant="stagger">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 3,
            }}
          >
            {filteredProducts.map((product) => (
              <motion.div key={product.id} variants={staggerChild} style={{ height: "100%" }}>
                <ProductCard product={product} />
              </motion.div>
            ))}
            {filteredProducts.length === 0 && (
              <Typography>No products found in this category.</Typography>
            )}
          </Box>
        </AnimatedInView>
      </Section>

      <CTABand
        title="Not sure which product fits your business?"
        subtitle="Tell us about your shop, restaurant or store — we'll recommend the right machine and set it up for you."
      />
    </>
  );
};

export default Products;
